import { useState, useMemo, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchAuditLogs } from '@/lib/auditLogApi';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  CaretLeft as ChevronLeft,
  PencilSimple as Edit,
  Trash,
  Envelope as Mail,
  Phone,
  Buildings as Building2,
  ShieldCheck,
  Tag,
  CalendarBlank as Calendar,
  Note as NoteIcon,
  CheckCircle,
  WarningCircle,
  X,
  FloppyDisk as Save,
  Briefcase,
  User,
} from '@phosphor-icons/react';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { useContactsStore } from '@/lib/contactsStore';
import { useMattersStore } from '@/lib/mattersStore';
import { useAuthStore } from '@/features/auth/store';
import { canMutateContacts } from '@/lib/pupilUiBoundary';
import { isValidName, isValidPhone, isValidEmail, sanitizeName, NAME_ERROR, PHONE_ERROR, EMAIL_ERROR } from '@/lib/validators';
import { PhoneInput } from '@/components/ui/phone-input';
import { ExpandableTextarea } from '@/components/ui/expandable-textarea';
import { ScrollLock } from '@/components/shared/ScrollLock';

// ─────────────────────────────────────────────────────────────
// Type labels + helpers
// ─────────────────────────────────────────────────────────────

const TYPE_LABEL: Record<string, string> = {
  lay: 'Lay client',
  solicitor: 'Instructing solicitor',
  expert: 'Expert',
  witness: 'Witness',
  company: 'Company',
};

function initials(name: string) {
  return name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatDate(ts: number) {
  if (!ts || ts < 100) {
    // seed data uses small ints; fall back to a plausible date
    const d = new Date(2026, 0, 10 + ts);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  }
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}



// ─────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────

export default function ContactDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isBarrister = ['silk', 'junior', 'pupil', 'door_tenant'].includes(user?.memberType ?? '');
  const basePath = isClerk ? '/clerk' : isBarrister ? '/barrister' : '/admin';
  const contactsListPath = isBarrister ? '/barrister/clients' : `${basePath}/contacts`;
  // Contacts are READ-ONLY for a pupil: `contact.create` / `contact.update.any` / `contact.delete`
  // are none of them granted (prisma/seed.ts), so Edit, Delete and the inline notes editor all
  // answered 403. The LIST page already withheld its equivalents via the same predicate
  // (MyClientsPage.tsx) — this page was the one that still offered them, and `saveEdit` below
  // announced success before the refusal arrived. Only pupil is subtracted; every other member
  // type keeps exactly what it had.
  const canEditContact = canMutateContacts(user?.memberType);
  const canDeleteContact = user?.memberType !== 'junior_clerk' && canEditContact;

  const contacts = useContactsStore((s) => s.contacts);
  const fetchContacts = useContactsStore((s) => s.fetchContacts);
  const updateContact = useContactsStore((s) => s.updateContact);
  const deleteContact = useContactsStore((s) => s.deleteContact);

  const allMatters = useMattersStore((s) => s.matters);
  const fetchMatters = useMattersStore((s) => s.fetchMatters);

  useEffect(() => { fetchContacts(); fetchMatters(); }, [fetchContacts, fetchMatters]);

  // Real audit log for this contact
  const auditQ = useQuery({
    queryKey: ['contact-audit', id],
    queryFn: () => fetchAuditLogs({ entityType: 'Contact', entityId: id, limit: 20 }),
    enabled: !!id,
    staleTime: 0,
  });
  const auditLogs = auditQ.data?.logs ?? [];

  const contact = contacts.find((c) => c.id === id);

  // Linked matters — real relational lookup via layClientId / instructingSolicitorId / opponentContactId
  const linkedMatters = useMemo(() => {
    if (!contact) return [];
    return allMatters.filter((m) =>
      m.parties?.some((p) => p.contactId === contact.id)
    );
  }, [contact, allMatters]);

  // Edit state
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    phone: '',
    org: '',
    notes: '',
  });

  // Delete confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Notes editing (standalone, separate from full edit)
  const [editingNotes, setEditingNotes] = useState(false);
  const [noteDraft, setNoteDraft] = useState('');

  // ─── Not found ───
  if (!contact) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
          <User className="h-8 w-8 text-muted-foreground" />
        </div>
        <h2 className="text-xl font-semibold text-foreground">Contact not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The contact you are looking for does not exist or may have been deleted.
        </p>
        <Button
          variant="outline"
          size="md"
          className="mt-6"
          onClick={() => navigate(contactsListPath)}
        >
          <ChevronLeft className="h-4 w-4" />
          Back to contacts
        </Button>
      </div>
    );
  }

  // ─── Handlers ───
  function startEdit() {
    if (!contact) return;
    setEditForm({
      name: contact.name,
      email: contact.email,
      phone: contact.phone,
      org: contact.org,
      notes: contact.notes,
    });
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
  }

  function saveEdit() {
    if (!contact) return;
    const trimmedName = editForm.name.trim();
    const trimmedEmail = editForm.email.trim();
    if (!trimmedName) {
      toast.error('Name is required');
      return;
    }
    if (!trimmedEmail) {
      toast.error('Email is required');
      return;
    }
    if (nameError || emailError || phoneError) {
      toast.error('Please correct the highlighted fields');
      return;
    }
    updateContact(contact.id, {
      name: trimmedName,
      email: trimmedEmail,
      phone: editForm.phone.trim(),
      org: editForm.org.trim(),
      notes: editForm.notes,
    });
    setEditing(false);
    toast.success('Contact updated');
  }

  function confirmDelete() {
    if (!contact) return;
    deleteContact(contact.id);
    toast.success(`${contact.name} deleted`);
    navigate(contactsListPath);
  }

  function startNotesEdit() {
    if (!contact) return;
    setNoteDraft(contact.notes);
    setEditingNotes(true);
  }

  function saveNotes() {
    if (!contact) return;
    updateContact(contact.id, { notes: noteDraft });
    setEditingNotes(false);
    toast.success('Notes saved');
  }

  // Field validation for the inline edit form. Validate name as a person's name
  // unless this is a company contact (whose name field holds the organisation name).
  const nameError = editForm.name.trim() && contact.type !== 'company' && !isValidName(editForm.name) ? NAME_ERROR : '';
  const emailError = editForm.email.trim() && !isValidEmail(editForm.email) ? EMAIL_ERROR : '';
  // Only once the user has CHANGED it — a stored number may predate the current rule, and
  // flagging an untouched field blocked every other edit on the record (the save at :190
  // refuses on any field error). Same reasoning as EditContactModal.
  const phoneError =
    editForm.phone !== (contact.phone ?? '') && editForm.phone.trim() && !isValidPhone(editForm.phone)
      ? PHONE_ERROR
      : '';


  return (
    <div>
      <PageHeader
        eyebrow={TYPE_LABEL[contact.type] ?? contact.type}
        title={contact.name}
        subtitle={`${contact.email} · ${contact.phone}`}
        onBack={() => navigate(contactsListPath)}
        backLabel="Back to contacts"
        actions={
          editing ? (
            <>
              <Button variant="outline" size="md" leftIcon={<X className="h-4 w-4" />} onClick={cancelEdit}>
                Cancel
              </Button>
              <Button variant="primary" size="md" leftIcon={<Save className="h-4 w-4" />} onClick={saveEdit}>
                Save changes
              </Button>
            </>
          ) : (
            <>
              {canEditContact && (
                <Button
                  variant="outline"
                  size="md"
                  leftIcon={<Edit className="h-4 w-4" />}
                  onClick={startEdit}
                >
                  Edit
                </Button>
              )}
              {canDeleteContact && (
                <Button
                  variant="destructive"
                  size="md"
                  leftIcon={<Trash className="h-4 w-4" />}
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete
                </Button>
              )}
            </>
          )
        }
      />

      {/* `min-w-0` on both columns: a grid item will not shrink below its content's minimum, so
          the widest thing inside — the linked-matters table and the contact's email address —
          held both columns open at 465px inside a 390px phone. The "View all matters" link,
          "Add note" and the Call button all sat off the right edge, unreachable. */}
      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        {/* ─── Main column ─── */}
        <div className="min-w-0 space-y-6">
          {/* Contact info card */}
          <Card>
            <CardHeader>
              <CardTitle>Contact information</CardTitle>
            </CardHeader>
            <CardContent>
              {editing ? (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Full name
                    </label>
                    <input
                      type="text"
                      maxLength={200}
                      className="mt-1 block w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: contact.type === 'company' ? e.target.value : sanitizeName(e.target.value) }))}
                    />
                    {nameError && <p className="mt-1 text-xs text-danger">{nameError}</p>}
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Email
                    </label>
                    <input
                      type="email"
                      maxLength={255}
                      className="mt-1 block w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
                      value={editForm.email}
                      onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                    />
                    {emailError && <p className="mt-1 text-xs text-danger">{emailError}</p>}
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Phone
                    </label>
                    <div className="mt-1">
                      <PhoneInput
                        value={editForm.phone}
                        onChange={(v) => setEditForm((f) => ({ ...f, phone: v }))}
                        error={phoneError || undefined}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Organisation
                    </label>
                    <input
                      type="text"
                      className="mt-1 block w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:border-brand-600 focus:outline-none focus:ring-1 focus:ring-brand-600"
                      value={editForm.org}
                      onChange={(e) => setEditForm((f) => ({ ...f, org: e.target.value }))}
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Notes
                    </label>
                    <div className="mt-1">
                      <ExpandableTextarea
                        label="Notes"
                        rows={3}
                        value={editForm.notes}
                        onChange={(v) => setEditForm((f) => ({ ...f, notes: v }))}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Email
                    </div>
                    <a
                      href={`mailto:${contact.email}`}
                      className="mt-1 flex items-center gap-1.5 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                    >
                      <Mail className="h-3.5 w-3.5" />
                      {contact.email}
                    </a>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Phone
                    </div>
                    <a
                      href={`tel:${contact.phone}`}
                      className="mt-1 flex items-center gap-1.5 text-sm text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                    >
                      <Phone className="h-3.5 w-3.5" />
                      {contact.phone}
                    </a>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      Organisation
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm text-foreground">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      {contact.org}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      KYC status
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm">
                      {contact.kyc === 'Valid' ? (
                        <CheckCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" weight="fill" />
                      ) : contact.kyc === 'Expiring' ? (
                        <WarningCircle className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" weight="fill" />
                      ) : (
                        <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />
                      )}
                      <Badge
                        variant={
                          contact.kyc === 'Valid'
                            ? 'success'
                            : contact.kyc === 'Expiring'
                              ? 'warning'
                              : 'default'
                        }
                      >
                        {contact.kyc}
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Linked matters */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <CardTitle>Linked matters</CardTitle>
                <Link
                  to={`${basePath}/matters`}
                  className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                >
                  View all matters
                </Link>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0">
              {linkedMatters.length > 0 ? (
                <div className="overflow-x-auto">
                  {/* Columns cannot reflow; on a phone the last ones were off-screen. */}
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Ref</th>
                        <th>Matter</th>
                        <th>Court</th>
                        <th>Barrister</th>
                        <th>Status</th>
                        <th>Next hearing</th>
                      </tr>
                    </thead>
                    <tbody>
                      {linkedMatters.map((m) => (
                        <tr
                          key={m.id}
                          className="cursor-pointer"
                          onClick={() => navigate(`${basePath}/matters/${m.id}`)}
                        >
                          <td className="text-xs text-brand-700 dark:text-brand-300">{m.number}</td>
                          <td className="font-medium text-foreground">{m.name}</td>
                          <td className="text-muted-foreground">{m.court}</td>
                          <td className="text-muted-foreground">{m.barrister}</td>
                          <td>
                            <Badge
                              variant={
                                m.status === 'active'
                                  ? 'success'
                                  : m.status === 'listed'
                                    ? 'info'
                                    : m.status === 'on_hold'
                                      ? 'warning'
                                      : m.status === 'closed_settled'
                                        ? 'default'
                                        : 'default'
                              }
                            >
                              {m.status.replace(/_/g, ' ')}
                            </Badge>
                          </td>
                          <td className="text-xs text-muted-foreground">{m.next}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No matters linked to this contact.
                </div>
              )}
            </CardContent>
          </Card>

          {/* Activity timeline — real audit log */}
          <Card>
            <CardHeader>
              <CardTitle>Activity</CardTitle>
            </CardHeader>
            <CardContent>
              {auditQ.isLoading ? (
                <p className="text-sm text-muted-foreground">Loading activity…</p>
              ) : auditLogs.length === 0 ? (
                <p className="text-sm text-muted-foreground">No activity recorded yet.</p>
              ) : (
                <div className="relative space-y-0">
                  {auditLogs.map((a, idx) => {
                    const isLast = idx === auditLogs.length - 1;
                    return (
                      <div key={a.id} className="relative flex gap-3 pb-6">
                        {!isLast && (
                          <div className="absolute left-[13px] top-7 bottom-0 w-px bg-border" />
                        )}
                        <div className="relative z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted">
                          <NoteIcon className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <p className="text-sm text-foreground capitalize">{a.action} by {a.actorEmail ?? 'System'}</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {new Date(a.createdAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* ─── Sidebar ─── */}
        <div className="min-w-0 space-y-4 lg:sticky lg:top-20 lg:self-start">
          {/* Quick info */}
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-col items-center text-center">
                <Avatar fallback={initials(contact.name)} size="xl" className="h-20 w-20 text-xl" />
                <h3 className="mt-3 text-lg font-semibold text-foreground">{contact.name}</h3>
                <div className="mt-1.5 flex items-center gap-2">
                  <Badge variant="info">{TYPE_LABEL[contact.type] ?? contact.type}</Badge>
                  <Badge
                    variant={
                      contact.kyc === 'Valid'
                        ? 'success'
                        : contact.kyc === 'Expiring'
                          ? 'warning'
                          : 'default'
                    }
                  >
                    KYC: {contact.kyc}
                  </Badge>
                </div>
                <div className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  Added {formatDate(contact.createdAt)}
                </div>
              </div>

              <div className="mt-5 grid grid-cols-2 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  leftIcon={<Mail className="h-3.5 w-3.5" />}
                  onClick={() => {
                    window.location.href = `mailto:${contact.email}`;
                  }}
                >
                  Email
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  leftIcon={<Phone className="h-3.5 w-3.5" />}
                  onClick={() => {
                    window.location.href = `tel:${contact.phone}`;
                  }}
                >
                  Call
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Tags */}
          <Card>
            <CardHeader>
              <CardTitle>Tags</CardTitle>
            </CardHeader>
            <CardContent>
              {contact.tags.length > 0 ? (
                <div className="flex flex-wrap gap-1.5">
                  {contact.tags.map((tag) => (
                    <Badge key={tag} variant="outline">
                      <Tag className="h-3 w-3" />
                      {tag}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">No tags assigned.</p>
              )}
            </CardContent>
          </Card>

          {/* Notes */}
          <Card>
            <CardHeader>
              <div className="flex items-start justify-between">
                <CardTitle>Notes</CardTitle>
                {/* Same `contact.update.any` key as the header's Edit — a pupil is refused here too. */}
                {!editingNotes && canEditContact && (
                  <button
                    onClick={startNotesEdit}
                    className="text-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300"
                  >
                    {contact.notes ? 'Edit' : 'Add note'}
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {editingNotes ? (
                <div>
                  <ExpandableTextarea
                    label="Notes"
                    rows={4}
                    placeholder="Add notes about this contact..."
                    value={noteDraft}
                    onChange={setNoteDraft}
                  />
                  <div className="mt-2 flex justify-end gap-2">
                    <Button
                      variant="ghost"
                      size="xs"
                      onClick={() => setEditingNotes(false)}
                    >
                      Cancel
                    </Button>
                    <Button variant="primary" size="xs" onClick={saveNotes}>
                      Save
                    </Button>
                  </div>
                </div>
              ) : contact.notes ? (
                <p className="max-h-[60vh] overflow-auto whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">{contact.notes}</p>
              ) : (
                <p className="text-sm text-muted-foreground">No notes yet.</p>
              )}
            </CardContent>
          </Card>

          {/* Matters count */}
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50">
                  <Briefcase className="h-4 w-4 text-brand-600 dark:text-brand-400" />
                </div>
                <div>
                  <div className="text-lg font-semibold text-foreground">{linkedMatters.length}</div>
                  <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                    Linked matters
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* ─── Delete confirmation modal ─── */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto p-4">
          <ScrollLock />
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50"
            onClick={() => setShowDeleteModal(false)}
          />
          {/* Modal */}
          <div className="relative mx-auto mt-[20vh] w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-50 dark:bg-red-500/10">
                <Trash className="h-5 w-5 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-foreground">Delete contact</h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  Are you sure you want to delete <strong>{contact.name}</strong>? This action cannot
                  be undone.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <Button variant="outline" size="md" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </Button>
              <Button variant="destructive" size="md" onClick={confirmDelete}>
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
